import { useEffect, useState, useCallback } from 'react';

// --- Types ---

type WSMessageType = 'BOT_STATUS' | 'TRADE' | 'POSITION' | 'HEARTBEAT';

interface WSMessage {
  type: WSMessageType;
  data: unknown;
}

type MessageHandler = (data: unknown) => void;

// --- Singleton connection state (shared across all hook instances) ---

let socket: WebSocket | null = null;
let refCount = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = 1000;
let keepaliveTimer: ReturnType<typeof setInterval> | null = null;

const MAX_RECONNECT_DELAY = 8000;
const KEEPALIVE_INTERVAL = 30000;
const WS_URL = import.meta.env.VITE_WS_URL || '';

// Subscribers: map from message type to set of handlers
const subscribers = new Map<WSMessageType, Set<MessageHandler>>();

// Connection state listeners (so each hook instance can track `connected`)
const connectedListeners = new Set<(val: boolean) => void>();
let currentConnected = false;

function setConnectedState(val: boolean) {
  currentConnected = val;
  connectedListeners.forEach((fn) => fn(val));
}

function dispatch(msg: WSMessage) {
  const handlers = subscribers.get(msg.type);
  if (handlers) {
    handlers.forEach((handler) => {
      try {
        handler(msg.data);
      } catch (err) {
        console.error(`[useWebSocket] Handler error for ${msg.type}:`, err);
      }
    });
  }
}

function clearTimers() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (keepaliveTimer) {
    clearInterval(keepaliveTimer);
    keepaliveTimer = null;
  }
}

function connect() {
  if (!WS_URL || socket) return;

  try {
    const ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      console.log('[useWebSocket] Connected');
      reconnectDelay = 1000; // reset backoff on successful connect
      setConnectedState(true);

      // Start keepalive pings
      keepaliveTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'PING' }));
        }
      }, KEEPALIVE_INTERVAL);
    };

    ws.onmessage = (event) => {
      try {
        const msg: WSMessage = JSON.parse(event.data);
        if (msg.type && msg.data !== undefined) {
          dispatch(msg);
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onclose = () => {
      console.log('[useWebSocket] Disconnected');
      cleanup();
      scheduleReconnect();
    };

    ws.onerror = () => {
      console.warn('[useWebSocket] Connection error');
      // onclose will fire after onerror, which handles cleanup + reconnect
    };

    socket = ws;
  } catch {
    console.warn('[useWebSocket] Failed to create WebSocket');
    scheduleReconnect();
  }
}

function cleanup() {
  if (keepaliveTimer) {
    clearInterval(keepaliveTimer);
    keepaliveTimer = null;
  }
  socket = null;
  setConnectedState(false);
}

function scheduleReconnect() {
  if (refCount <= 0 || reconnectTimer) return;

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, reconnectDelay);

  // Exponential backoff: 1s -> 2s -> 4s -> 8s (max)
  reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
}

function disconnect() {
  clearTimers();
  if (socket) {
    socket.onclose = null; // prevent reconnect from cleanup
    socket.close();
    socket = null;
  }
  setConnectedState(false);
  reconnectDelay = 1000;
}

// --- Hook ---

export function useWebSocket() {
  const [connected, setConnected] = useState(currentConnected);

  // Track this hook instance in the refcount + listen for connection state
  useEffect(() => {
    refCount++;
    if (refCount === 1) {
      connect();
    }

    const listener = (val: boolean) => setConnected(val);
    connectedListeners.add(listener);
    // Sync initial state
    setConnected(currentConnected);

    return () => {
      connectedListeners.delete(listener);
      refCount--;
      if (refCount <= 0) {
        refCount = 0;
        disconnect();
      }
    };
  }, []);

  const subscribe = useCallback((type: WSMessageType, handler: MessageHandler) => {
    let handlers = subscribers.get(type);
    if (!handlers) {
      handlers = new Set();
      subscribers.set(type, handlers);
    }
    handlers.add(handler);
  }, []);

  const unsubscribe = useCallback((type: WSMessageType, handler: MessageHandler) => {
    const handlers = subscribers.get(type);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        subscribers.delete(type);
      }
    }
  }, []);

  return { connected, subscribe, unsubscribe };
}
